import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { dispatch, useSelector } from 'store';
import { closeSubDrawer } from 'store/reducers/subdrawer';
import { closeTemporaryDrawer } from 'store/reducers/temporaryDrawer';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { DrawerFooter } from './DrawerFooter';
import { DrawerHeader } from './DrawerHeader';

type Props = {
    content: React.ReactNode;
    drawerId: string;
    /** The panel's width: a number is px, a string any CSS width. */
    width?: number | string;
    /** Below the small breakpoint, take the viewport less its gutters instead of `width`. */
    widensBelowSm?: boolean;
    /** Size the panel to its content rather than to the drawer's full height. */
    fitsContent?: boolean;
    titleId?: string;
    onFooterSecondaryAction?: () => void;
    onFooterPrimaryAction?: () => Promise<void>;
    applyColor?: 'warning' | 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info';
    afterTitle?: React.ReactNode;
    beforeFooterActions?: React.ReactNode;
    type: 'sub' | 'temporary';
    /**
     * Keep the content while the drawer's own panel transitions out, as every panel animates its close. Off, the
     * content goes at once and the panel slides out empty.
     */
    keepsContentWhileClosing?: boolean;
    /**
     * The id of the title the content draws itself. Focus moves to the panel as it opens: named by its title,
     * it is announced as a dialog of that name rather than as nothing.
     */
    labelledBy?: string;
};

const DrawerPortal: React.FC<Props> = (props) => {
    const {
        content,
        drawerId,
        titleId,
        width = 310,
        widensBelowSm = false,
        fitsContent = false,
        onFooterSecondaryAction,
        onFooterPrimaryAction,
        applyColor,
        afterTitle,
        beforeFooterActions,
        type,
        keepsContentWhileClosing = true,
        labelledBy,
    } = props;

    const activeSubDrawerId = useSelector((state) => state.subDrawer.activeDrawerId === drawerId);
    const activeTemporaryDrawerId = useSelector((state) => state.temporaryDrawer.activeDrawerId === drawerId);

    const isOpenSubDrawer = activeSubDrawerId && type === 'sub';
    const isOpenTemporaryDrawer = activeTemporaryDrawerId && type === 'temporary';

    const isOpen = isOpenSubDrawer || isOpenTemporaryDrawer;
    // Another drawer of the same type is open in the shared destination: content kept while closing would sit
    // above its content there, so it goes at once, and stays gone when that drawer closes in turn.
    const isAnotherOpen = useSelector((state) => {
        const activeId = type === 'sub' ? state.subDrawer.activeDrawerId : state.temporaryDrawer.activeDrawerId;
        return activeId !== '' && activeId !== drawerId;
    });

    const portalDestination =
        type === 'sub'
            ? document.querySelector('#sub-drawer-portal-destination')
            : document.querySelector('#temporary-drawer-portal-destination');

    // Closed but still on screen: the panel holding the destination is transitioning out.
    const [isClosing, setIsClosing] = useState(false);
    const [wasOpen, setWasOpen] = useState(isOpen);
    if (wasOpen !== isOpen) {
        setWasOpen(isOpen);
        setIsClosing(!isOpen && keepsContentWhileClosing && !isAnotherOpen);
    } else if (isClosing && isAnotherOpen) {
        setIsClosing(false);
    }
    useEffect(() => {
        if (!isClosing) return undefined;
        let current = true;
        const panel = portalDestination?.parentElement;
        // Settled rather than finished: reopening mid-way cancels the transition, and this effect with it.
        const transitions = typeof panel?.getAnimations === 'function' ? panel.getAnimations() : [];
        void Promise.allSettled(transitions.map((transition) => transition.finished)).then(() => {
            if (current) setIsClosing(false);
        });
        return () => {
            current = false;
        };
    }, [isClosing, portalDestination]);

    const closeDrawer = useCallback(() => {
        if (type === 'sub') dispatch(closeSubDrawer());
        else dispatch(closeTemporaryDrawer());
    }, [type]);

    // Non-modal, so nothing traps focus: opening moves it into the panel, closing hands it back to
    // what opened the drawer — unless something else has taken it since, such as a page navigated to.
    const panelRef = useRef<HTMLDivElement | null>(null);
    const openerRef = useRef<HTMLElement | null>(null);
    useEffect(() => {
        const panel = panelRef.current;
        if (isOpen && panel) {
            const focused = document.activeElement;
            if (!panel.contains(focused)) {
                openerRef.current = focused instanceof HTMLElement && focused !== document.body ? focused : null;
                panel.focus({ preventScroll: true });
            }
            return;
        }
        const opener = openerRef.current;
        openerRef.current = null;
        // Focus left with the panel when it lands on the body, on an element no longer in the document, or
        // is still in a panel on its way out.
        const focused = document.activeElement;
        if (
            opener?.isConnected &&
            (!focused || focused === document.body || !focused.isConnected || panel?.contains(focused))
        ) {
            opener.focus({ preventScroll: true });
        }
    }, [isOpen]);

    // Escape is heard on the window, the last stop of a keydown: a popup open inside the drawer has closed
    // on the same Escape by then and marked it handled or stopped it, so the drawer only closes on one
    // nothing else took, and only when it came from inside the panel.
    useEffect(() => {
        const panel = panelRef.current;
        if (!isOpen || !panel) {
            return undefined;
        }
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !event.defaultPrevented && panel.contains(event.target as Node)) {
                closeDrawer();
            }
        };
        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [isOpen, closeDrawer]);

    const handleFooterSecondary = onFooterSecondaryAction
        ? () => {
              onFooterSecondaryAction();
          }
        : undefined;

    const handleFooterPrimary = onFooterPrimaryAction
        ? () => {
              onFooterPrimaryAction().then(() => closeDrawer());
          }
        : undefined;

    if (portalDestination === null) {
        return null;
    }

    return isOpen || isClosing
        ? createPortal(
              <div
                  ref={panelRef}
                  tabIndex={-1}
                  role={labelledBy ? 'dialog' : undefined}
                  aria-labelledby={labelledBy}
                  inert={!isOpen}
                  className={cn(
                      // One `minmax(0, 1fr)` column in the new design: an implicit column would grow to the content's
                      // width, and the panel would cut off what it frames.
                      'new:grid-cols-[minmax(0,1fr)] grid max-w-[calc(100vw-48px)] grid-rows-[auto_1fr_auto] overflow-hidden outline-none',
                      !fitsContent && 'h-full',
                      // Important, since it is a class outranking the inline width it replaces.
                      widensBelowSm && 'max-sm:w-[calc(100vw-48px)]!'
                  )}
                  style={{ width }}
              >
                  {titleId ? <DrawerHeader title={t(titleId)} onCancel={closeDrawer} afterTitle={afterTitle} /> : null}
                  {content}
                  <DrawerFooter
                      onSecondary={handleFooterSecondary}
                      onPrimary={handleFooterPrimary}
                      applyColor={applyColor}
                      beforeActions={beforeFooterActions}
                  />
              </div>,
              portalDestination
          )
        : null;
};

export default DrawerPortal;
